// Ported from EnterpriseTemplates react-project-template (Release 1.3.1), PMS slice 4b.
// ============================================================================
// ORGANISATION SCREEN DEFINITIONS — entities, business units, financial years
// ============================================================================
// Each definition supplies what differs per kind: columns, form fields,
// validation, request shapes, view sections. OrganisationMasterScreen supplies
// everything else. Type codes come from the application's own lookup tables
// (GET /org/lookups); codes are fixed after creation; parent and status change
// through the master's patch; a financial year's current/closed flags change in
// Tenant Hub and arrive by sync.

import React from 'react';
import { SyutSelect, type SelectOption, type SyutGridColumn, type ColumnDefinition } from 'components/ui';
import { organisationService } from 'api/services/organisationService';
import {
  PROJECTION_STATUS_OPTIONS,
  type ProjectedEntity, type ProjectedBusinessUnit, type ProjectedFinancialYear,
  type CreateEntityCommand, type UpdateEntityCommand,
  type CreateBusinessUnitCommand, type UpdateBusinessUnitCommand,
  type CreateFinancialYearCommand, type UpdateFinancialYearCommand,
  type ProjectionStatusCode, type OrganisationTypeLookup,
} from 'api/types/organisation.types';
import type { OrganisationScreenDefinition, FieldArgs } from './organisationScreen.types';

// ─── Shared field helpers ─────────────────────────────────────────────────────

const TextField: React.FC<{
  field: string; label: string; value: string; required?: boolean; maxLength?: number;
  type?: 'text' | 'date'; disabled?: boolean; placeholder?: string;
  error?: string; onChange: (value: string) => void;
}> = ({ field, label, value, required, maxLength, type = 'text', disabled, placeholder, error, onChange }) => (
  <div className="form-group" data-field={field}>
    <label className="form-label" htmlFor={`org-field-${field}`}>{label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}</label>
    <input
      id={`org-field-${field}`}
      type={type}
      className={`form-input ${error ? 'error' : ''}`}
      value={value}
      maxLength={maxLength}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
    {error && <span className="form-error">{error}</span>}
  </div>
);

const StatusField: React.FC<{ value: string; onChange: (v: ProjectionStatusCode) => void }> = ({ value, onChange }) => (
  <div className="form-group" data-field="statusCode">
    <SyutSelect
      label="Status"
      options={PROJECTION_STATUS_OPTIONS}
      value={value}
      onChange={(v) => onChange(((v as string) || 'ACTIVE') as ProjectionStatusCode)}
      isClearable={false}
      size="md"
    />
  </div>
);

const typeOptions = (types: OrganisationTypeLookup[]): SelectOption[] =>
  types.map(t => ({ value: t.typeCode, label: `${t.typeName} (${t.typeCode})` }));

/** Async option loader over the projection rows of a kind, excluding one id (the row itself). */
const projectionLoader = <T extends { id: string }>(
  kind: 'entities' | 'business-units',
  label: (row: T) => string,
  excludeId?: string,
  keep?: (row: T) => boolean,
) => async (inputValue: string): Promise<SelectOption[]> => {
  try {
    const res = await organisationService.list<T>(kind, { search: inputValue || undefined, page: 1, pageSize: 50 });
    return res.data.filter(r => r.id !== excludeId && (!keep || keep(r))).map(r => ({ value: r.id, label: label(r) }));
  } catch {
    return [];
  }
};

const dateOnly = (value?: string | null) => (value ? value.slice(0, 10) : '');
const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleDateString() : '-');

/**
 * ACTIVE / INACTIVE badge used by every organisation grid and view. A live row with no status recorded (created before the projection, e.g. by the Fairbooks integration
 * setup script) reads as Active: the edit form already treats it so, and only INACTIVE switches a row off.
 */
export const ProjectionStatusBadge: React.FC<{ statusCode?: string | null }> = ({ statusCode }) => {
  const active = !statusCode || statusCode.toUpperCase() === 'ACTIVE';
  return (
    <span className={`badge ${active ? 'badge-active' : 'badge-inactive'}`}
      title={statusCode ? undefined : 'No status recorded; treated as active'}>
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

const CanonicalId: React.FC<{ value?: string | null }> = ({ value }) =>
  value ? <code style={{ fontSize: '0.8rem' }}>{value}</code> : <span style={{ color: 'var(--danger)' }}>None — not registered with the organisation master</span>;

const masterSection = (r: { canonicalId?: string | null; rowVersion: number; updatedAt?: string | null }) => ({
  title: 'Organisation master', icon: 'link', rows: [
    { label: 'Canonical id', value: <CanonicalId value={r.canonicalId} /> },
    { label: 'Local version', value: r.rowVersion },
    { label: 'Updated', value: formatDate(r.updatedAt) },
  ],
});

// ============================================================================
// ENTITIES
// ============================================================================

export interface EntityForm {
  entityCode: string;
  entityName: string;
  entityTypeCode: string;
  taxNumber: string;
  registrationNumber: string;
  statusCode: ProjectionStatusCode;
}

const EMPTY_ENTITY: EntityForm = {
  entityCode: '', entityName: '', entityTypeCode: '', taxNumber: '', registrationNumber: '', statusCode: 'ACTIVE',
};

const entityColumnDefinitions: ColumnDefinition[] = [
  { id: 'code', label: 'Code', defaultVisible: true },
  { id: 'name', label: 'Name', defaultVisible: true },
  { id: 'type', label: 'Type', defaultVisible: true },
  { id: 'taxNumber', label: 'Tax number', defaultVisible: false },
  { id: 'status', label: 'Status', defaultVisible: true },
  { id: 'canonical', label: 'Canonical id', defaultVisible: false },
  { id: 'actions', label: 'Actions', required: true, defaultVisible: true, width: 80 },
];

const entityGridColumns: SyutGridColumn<ProjectedEntity>[] = [
  { id: 'code', header: 'Code', accessorKey: 'entityCode', size: 120 },
  { id: 'name', header: 'Name', accessorKey: 'entityName', size: 240 },
  { id: 'type', header: 'Type', accessorKey: 'entityTypeCode', size: 150 },
  { id: 'taxNumber', header: 'Tax number', accessorKey: 'taxNumber', size: 150 },
  { id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110, cell: ({ row }) => <ProjectionStatusBadge statusCode={row.original.statusCode} /> },
  { id: 'canonical', header: 'Canonical id', accessorKey: 'canonicalId', size: 300, cell: ({ getValue }) => <code style={{ fontSize: '0.75rem' }}>{(getValue() as string) || '—'}</code> },
];

export const entityDefinition: OrganisationScreenDefinition<ProjectedEntity, EntityForm> = {
  kind: 'entities',
  screenCode: 'ORG_ENTITY',
  title: 'Entities',
  description: 'Legal entities of the organisation, written through to the Tenant Hub organisation master',
  noun: 'Entity',
  moduleName: 'Entities',
  searchPlaceholder: 'Search entities by code or name...',
  columnsStorageKey: 'org-entities-columns',
  defaultIcon: 'home',
  columnDefinitions: entityColumnDefinitions,
  gridColumns: entityGridColumns,
  recordCode: r => r.entityCode,
  recordName: r => r.entityName,
  emptyForm: EMPTY_ENTITY,
  populate: (mode, record) => (mode === 'edit' && record) ? {
    entityCode: record.entityCode,
    entityName: record.entityName,
    entityTypeCode: record.entityTypeCode || '',
    taxNumber: record.taxNumber || '',
    registrationNumber: record.registrationNumber || '',
    statusCode: ((record.statusCode || 'ACTIVE').toUpperCase() as ProjectionStatusCode),
  } : EMPTY_ENTITY,
  validate: (form, mode) => {
    const errors: Record<string, string> = {};
    if (!form.entityCode.trim()) errors.entityCode = 'Code is required';
    else if (form.entityCode.trim().length > 50) errors.entityCode = 'Code must be 50 characters or fewer';
    if (!form.entityName.trim()) errors.entityName = 'Name is required';
    if (mode === 'create' && !form.entityTypeCode) errors.entityTypeCode = 'Type is required';
    return errors;
  },
  toCreate: (form): CreateEntityCommand => ({
    entityCode: form.entityCode.trim(),
    entityName: form.entityName.trim(),
    entityTypeCode: form.entityTypeCode,
    taxNumber: form.taxNumber.trim() || null,
    registrationNumber: form.registrationNumber.trim() || null,
  }),
  toUpdate: (form): UpdateEntityCommand => ({
    entityName: form.entityName.trim(),
    statusCode: form.statusCode,
  }),
  renderFields: ({ form, setForm, mode, lookups, getFieldError, clearFieldError }: FieldArgs<EntityForm, ProjectedEntity>) => {
    const set = (patch: Partial<EntityForm>) => setForm(prev => ({ ...prev, ...patch }));
    return (
      <>
        <div className="form-grid-2">
          <TextField field="entityCode" label="Code" value={form.entityCode} required maxLength={50} disabled={mode === 'edit'}
            error={getFieldError('entityCode')} onChange={(v) => { clearFieldError('entityCode'); set({ entityCode: v.toUpperCase() }); }} />
          <TextField field="entityName" label="Name" value={form.entityName} required maxLength={200}
            error={getFieldError('entityName')} onChange={(v) => { clearFieldError('entityName'); set({ entityName: v }); }} />
        </div>
        <div className="form-grid-2">
          <div className="form-group" data-field="entityTypeCode">
            <SyutSelect label="Type" required options={typeOptions(lookups.entityTypes)} value={form.entityTypeCode}
              onChange={(v) => { clearFieldError('entityTypeCode'); set({ entityTypeCode: (v as string) || '' }); }}
              error={getFieldError('entityTypeCode')} isDisabled={mode === 'edit'} isClearable={false} size="md"
              placeholder="Select the entity type" />
          </div>
          {mode === 'edit' ? (
            <StatusField value={form.statusCode} onChange={(v) => set({ statusCode: v })} />
          ) : (
            <TextField field="taxNumber" label="Tax number" value={form.taxNumber} maxLength={100} onChange={(v) => set({ taxNumber: v })} />
          )}
        </div>
        {mode === 'create' && (
          <TextField field="registrationNumber" label="Registration number" value={form.registrationNumber} maxLength={100} onChange={(v) => set({ registrationNumber: v })} />
        )}
        {mode === 'edit' && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            The code and type are fixed once the organisation master has issued the row. Name and status are written through to the master.
          </div>
        )}
      </>
    );
  },
  viewSections: (r) => [
    { title: 'General', icon: 'info', rows: [
      { label: 'Code', value: r.entityCode },
      { label: 'Name', value: r.entityName },
      { label: 'Type', value: r.entityTypeCode },
      { label: 'Tax number', value: r.taxNumber },
      { label: 'Registration number', value: r.registrationNumber },
      { label: 'Status', value: <ProjectionStatusBadge statusCode={r.statusCode} /> },
    ] },
    masterSection(r),
  ],
};

// ============================================================================
// BUSINESS UNITS
// ============================================================================

export interface BusinessUnitForm {
  entityId: string;
  parentUnitId: string;
  unitCode: string;
  unitName: string;
  unitTypeCode: string;
  statusCode: ProjectionStatusCode;
}

const EMPTY_BU: BusinessUnitForm = {
  entityId: '', parentUnitId: '', unitCode: '', unitName: '', unitTypeCode: '', statusCode: 'ACTIVE',
};

const buColumnDefinitions: ColumnDefinition[] = [
  { id: 'code', label: 'Code', defaultVisible: true },
  { id: 'name', label: 'Name', defaultVisible: true },
  { id: 'entity', label: 'Entity', defaultVisible: true },
  { id: 'type', label: 'Type', defaultVisible: true },
  { id: 'parent', label: 'Parent unit', defaultVisible: true },
  { id: 'status', label: 'Status', defaultVisible: true },
  { id: 'canonical', label: 'Canonical id', defaultVisible: false },
  { id: 'actions', label: 'Actions', required: true, defaultVisible: true, width: 80 },
];

const buGridColumns: SyutGridColumn<ProjectedBusinessUnit>[] = [
  { id: 'code', header: 'Code', accessorKey: 'unitCode', size: 120 },
  { id: 'name', header: 'Name', accessorKey: 'unitName', size: 220 },
  { id: 'entity', header: 'Entity', accessorKey: 'entityCode', size: 140 },
  { id: 'type', header: 'Type', accessorKey: 'unitTypeCode', size: 140 },
  { id: 'parent', header: 'Parent unit', accessorKey: 'parentUnitCode', size: 140, cell: ({ getValue }) => (getValue() as string) || '-' },
  { id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110, cell: ({ row }) => <ProjectionStatusBadge statusCode={row.original.statusCode} /> },
  { id: 'canonical', header: 'Canonical id', accessorKey: 'canonicalId', size: 300, cell: ({ getValue }) => <code style={{ fontSize: '0.75rem' }}>{(getValue() as string) || '—'}</code> },
];

export const businessUnitDefinition: OrganisationScreenDefinition<ProjectedBusinessUnit, BusinessUnitForm> = {
  kind: 'business-units',
  screenCode: 'ORG_BUSINESS_UNIT',
  title: 'Business Units',
  description: 'Business units within the entities, written through to the Tenant Hub organisation master',
  noun: 'Business Unit',
  moduleName: 'Business Units',
  searchPlaceholder: 'Search business units by code, name or entity...',
  columnsStorageKey: 'org-business-units-columns',
  defaultIcon: 'grid',
  columnDefinitions: buColumnDefinitions,
  gridColumns: buGridColumns,
  recordCode: r => r.unitCode,
  recordName: r => r.unitName,
  emptyForm: EMPTY_BU,
  populate: (mode, record) => (mode === 'edit' && record) ? {
    entityId: record.entityId,
    parentUnitId: record.parentUnitId || '',
    unitCode: record.unitCode,
    unitName: record.unitName,
    unitTypeCode: record.unitTypeCode || '',
    statusCode: ((record.statusCode || 'ACTIVE').toUpperCase() as ProjectionStatusCode),
  } : EMPTY_BU,
  validate: (form, mode) => {
    const errors: Record<string, string> = {};
    if (mode === 'create' && !form.entityId) errors.entityId = 'Entity is required';
    if (!form.unitCode.trim()) errors.unitCode = 'Code is required';
    else if (form.unitCode.trim().length > 50) errors.unitCode = 'Code must be 50 characters or fewer';
    if (!form.unitName.trim()) errors.unitName = 'Name is required';
    if (mode === 'create' && !form.unitTypeCode) errors.unitTypeCode = 'Type is required';
    return errors;
  },
  toCreate: (form): CreateBusinessUnitCommand => ({
    entityId: form.entityId,
    parentUnitId: form.parentUnitId || null,
    unitCode: form.unitCode.trim(),
    unitName: form.unitName.trim(),
    unitTypeCode: form.unitTypeCode,
  }),
  toUpdate: (form, record): UpdateBusinessUnitCommand => ({
    unitName: form.unitName.trim(),
    parentUnitId: form.parentUnitId || null,
    clearParent: !form.parentUnitId && !!record.parentUnitId,
    statusCode: form.statusCode,
  }),
  renderFields: ({ form, setForm, mode, record, lookups, getFieldError, clearFieldError }: FieldArgs<BusinessUnitForm, ProjectedBusinessUnit>) => {
    const set = (patch: Partial<BusinessUnitForm>) => setForm(prev => ({ ...prev, ...patch }));
    return (
      <>
        <div className="form-group" data-field="entityId">
          <SyutSelect
            label="Entity"
            required
            isAsync
            loadOptions={projectionLoader<ProjectedEntity>('entities', r => `${r.entityCode} - ${r.entityName}`)}
            defaultOptions
            cacheOptions={false}
            seedOptions={record?.entityId && record.entityCode ? [{ value: record.entityId, label: record.entityCode }] : undefined}
            value={form.entityId}
            onChange={(v) => { clearFieldError('entityId'); set({ entityId: (v as string) || '', parentUnitId: '' }); }}
            error={getFieldError('entityId')}
            placeholder="Select the entity this unit belongs to"
            isDisabled={mode === 'edit'}
            isClearable={false}
            size="md"
          />
        </div>
        <div className="form-grid-2">
          <TextField field="unitCode" label="Code" value={form.unitCode} required maxLength={50} disabled={mode === 'edit'}
            error={getFieldError('unitCode')} onChange={(v) => { clearFieldError('unitCode'); set({ unitCode: v.toUpperCase() }); }} />
          <TextField field="unitName" label="Name" value={form.unitName} required maxLength={200}
            error={getFieldError('unitName')} onChange={(v) => { clearFieldError('unitName'); set({ unitName: v }); }} />
        </div>
        <div className="form-grid-2">
          <div className="form-group" data-field="unitTypeCode">
            <SyutSelect label="Type" required options={typeOptions(lookups.businessUnitTypes)} value={form.unitTypeCode}
              onChange={(v) => { clearFieldError('unitTypeCode'); set({ unitTypeCode: (v as string) || '' }); }}
              error={getFieldError('unitTypeCode')} isDisabled={mode === 'edit'} isClearable={false} size="md"
              placeholder="Select the unit type" />
          </div>
          <div className="form-group" data-field="parentUnitId">
            <SyutSelect
              label="Parent unit"
              isAsync
              loadOptions={projectionLoader<ProjectedBusinessUnit>('business-units', r => `${r.unitCode} - ${r.unitName}`, record?.id, r => r.entityId === form.entityId)}
              defaultOptions
              cacheOptions={false}
              seedOptions={record?.parentUnitId && record.parentUnitCode ? [{ value: record.parentUnitId, label: record.parentUnitCode }] : undefined}
              value={form.parentUnitId}
              onChange={(v) => set({ parentUnitId: (v as string) || '' })}
              placeholder={form.entityId ? 'None (top level)' : 'Choose the entity first'}
              isDisabled={!form.entityId}
              isClearable
              size="md"
            />
          </div>
        </div>
        {mode === 'edit' && <StatusField value={form.statusCode} onChange={(v) => set({ statusCode: v })} />}
      </>
    );
  },
  viewSections: (r) => [
    { title: 'General', icon: 'info', rows: [
      { label: 'Code', value: r.unitCode },
      { label: 'Name', value: r.unitName },
      { label: 'Entity', value: r.entityCode },
      { label: 'Type', value: r.unitTypeCode },
      { label: 'Parent unit', value: r.parentUnitCode || 'None (top level)' },
      { label: 'Status', value: <ProjectionStatusBadge statusCode={r.statusCode} /> },
    ] },
    masterSection(r),
  ],
};

// ============================================================================
// FINANCIAL YEARS
// ============================================================================

export interface FinancialYearForm {
  fyCode: string;
  fyName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  statusCode: ProjectionStatusCode;
}

const EMPTY_FY: FinancialYearForm = {
  fyCode: '', fyName: '', startDate: '', endDate: '', isCurrent: false, statusCode: 'ACTIVE',
};

const fyColumnDefinitions: ColumnDefinition[] = [
  { id: 'code', label: 'Code', defaultVisible: true },
  { id: 'name', label: 'Name', defaultVisible: true },
  { id: 'startDate', label: 'Start', defaultVisible: true },
  { id: 'endDate', label: 'End', defaultVisible: true },
  { id: 'current', label: 'Current', defaultVisible: true },
  { id: 'closed', label: 'Closed', defaultVisible: true },
  { id: 'status', label: 'Status', defaultVisible: true },
  { id: 'canonical', label: 'Canonical id', defaultVisible: false },
  { id: 'actions', label: 'Actions', required: true, defaultVisible: true, width: 80 },
];

const YesNo: React.FC<{ value: boolean; yesClass?: string }> = ({ value, yesClass = 'badge-active' }) => (
  value ? <span className={`badge ${yesClass}`}>Yes</span> : <span style={{ color: 'var(--text-muted)' }}>No</span>
);

const fyGridColumns: SyutGridColumn<ProjectedFinancialYear>[] = [
  { id: 'code', header: 'Code', accessorKey: 'fyCode', size: 130 },
  { id: 'name', header: 'Name', accessorKey: 'fyName', size: 200 },
  { id: 'startDate', header: 'Start', accessorKey: 'startDate', size: 120, cell: ({ getValue }) => formatDate(getValue() as string) },
  { id: 'endDate', header: 'End', accessorKey: 'endDate', size: 120, cell: ({ getValue }) => formatDate(getValue() as string) },
  { id: 'current', header: 'Current', accessorKey: 'isCurrent', size: 90, cell: ({ row }) => <YesNo value={row.original.isCurrent} /> },
  { id: 'closed', header: 'Closed', accessorKey: 'isClosed', size: 90, cell: ({ row }) => <YesNo value={row.original.isClosed} yesClass="badge-inactive" /> },
  { id: 'status', header: 'Status', accessorKey: 'statusCode', size: 110, cell: ({ row }) => <ProjectionStatusBadge statusCode={row.original.statusCode} /> },
  { id: 'canonical', header: 'Canonical id', accessorKey: 'canonicalId', size: 300, cell: ({ getValue }) => <code style={{ fontSize: '0.75rem' }}>{(getValue() as string) || '—'}</code> },
];

export const financialYearDefinition: OrganisationScreenDefinition<ProjectedFinancialYear, FinancialYearForm> = {
  kind: 'financial-years',
  screenCode: 'ORG_FINANCIAL_YEAR',
  title: 'Financial Years',
  description: 'Financial year periods of the organisation, written through to the Tenant Hub organisation master',
  noun: 'Financial Year',
  moduleName: 'Financial Years',
  searchPlaceholder: 'Search financial years by code or name...',
  columnsStorageKey: 'org-financial-years-columns',
  defaultIcon: 'calendar',
  formSize: 'sm',
  columnDefinitions: fyColumnDefinitions,
  gridColumns: fyGridColumns,
  recordCode: r => r.fyCode,
  recordName: r => r.fyName,
  emptyForm: EMPTY_FY,
  populate: (mode, record) => (mode === 'edit' && record) ? {
    fyCode: record.fyCode,
    fyName: record.fyName,
    startDate: dateOnly(record.startDate),
    endDate: dateOnly(record.endDate),
    isCurrent: record.isCurrent,
    statusCode: ((record.statusCode || 'ACTIVE').toUpperCase() as ProjectionStatusCode),
  } : EMPTY_FY,
  validate: (form, mode) => {
    const errors: Record<string, string> = {};
    if (!form.fyCode.trim()) errors.fyCode = 'Code is required';
    else if (form.fyCode.trim().length > 20) errors.fyCode = 'Code must be 20 characters or fewer';
    if (!form.fyName.trim()) errors.fyName = 'Name is required';
    if (mode === 'create') {
      if (!form.startDate) errors.startDate = 'Start date is required';
      if (!form.endDate) errors.endDate = 'End date is required';
      if (form.startDate && form.endDate && form.endDate <= form.startDate) errors.endDate = 'End date must be after the start date';
    }
    return errors;
  },
  toCreate: (form): CreateFinancialYearCommand => ({
    fyCode: form.fyCode.trim(),
    fyName: form.fyName.trim(),
    startDate: form.startDate,
    endDate: form.endDate,
    isCurrent: form.isCurrent,
  }),
  toUpdate: (form): UpdateFinancialYearCommand => ({
    fyName: form.fyName.trim(),
    statusCode: form.statusCode,
  }),
  renderFields: ({ form, setForm, mode, getFieldError, clearFieldError }: FieldArgs<FinancialYearForm, ProjectedFinancialYear>) => {
    const set = (patch: Partial<FinancialYearForm>) => setForm(prev => ({ ...prev, ...patch }));
    return (
      <>
        <div className="form-grid-2">
          <TextField field="fyCode" label="Code" value={form.fyCode} required maxLength={20} disabled={mode === 'edit'} placeholder="FY2026"
            error={getFieldError('fyCode')} onChange={(v) => { clearFieldError('fyCode'); set({ fyCode: v.toUpperCase() }); }} />
          <TextField field="fyName" label="Name" value={form.fyName} required maxLength={100}
            error={getFieldError('fyName')} onChange={(v) => { clearFieldError('fyName'); set({ fyName: v }); }} />
        </div>
        <div className="form-grid-2">
          <TextField field="startDate" label="Start date" type="date" value={form.startDate} required disabled={mode === 'edit'}
            error={getFieldError('startDate')} onChange={(v) => { clearFieldError('startDate'); clearFieldError('endDate'); set({ startDate: v }); }} />
          <TextField field="endDate" label="End date" type="date" value={form.endDate} required disabled={mode === 'edit'}
            error={getFieldError('endDate')} onChange={(v) => { clearFieldError('endDate'); set({ endDate: v }); }} />
        </div>
        {mode === 'create' ? (
          <div className="form-group" data-field="isCurrent">
            <label className="form-label" htmlFor="org-field-isCurrent" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input id="org-field-isCurrent" type="checkbox" checked={form.isCurrent} onChange={(e) => set({ isCurrent: e.target.checked })} />
              Make this the current financial year
            </label>
          </div>
        ) : (
          <>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
              Current: <strong>{form.isCurrent ? 'Yes' : 'No'}</strong>. The current and closed flags are set in Tenant Hub and arrive here by sync; dates are fixed once the master has issued the year.
            </div>
            <StatusField value={form.statusCode} onChange={(v) => set({ statusCode: v })} />
          </>
        )}
      </>
    );
  },
  viewSections: (r) => [
    { title: 'General', icon: 'calendar', rows: [
      { label: 'Code', value: r.fyCode },
      { label: 'Name', value: r.fyName },
      { label: 'Start date', value: formatDate(r.startDate) },
      { label: 'End date', value: formatDate(r.endDate) },
      { label: 'Current', value: <YesNo value={r.isCurrent} /> },
      { label: 'Closed', value: <YesNo value={r.isClosed} yesClass="badge-inactive" /> },
      { label: 'Status', value: <ProjectionStatusBadge statusCode={r.statusCode} /> },
    ] },
    masterSection(r),
  ],
};
